import type { MechanicDefinition } from "./types";

export const skillChecks: MechanicDefinition = {
  id: "skill_checks",
  disabled: true,
  name: "Stats & Skill Checks",
  description: "Character ability scores with dice-based skill resolution, advantage/disadvantage, and situational modifiers",
  icon: "Dices",
  category: "systems",
  aiHints: {
    purpose: "Defines character ability scores and dice-based resolution for non-combat actions like perception, persuasion, athletics.",
    whenToUse: "Almost every RPG world benefits from this. Core foundation for stat-based gameplay.",
    synergies: ["combat", "saving_throws", "reputation", "inventory", "survival"],
    conflicts: [],
    prerequisites: [],
    tags: ["stats", "dice", "skills", "ability-scores", "foundation", "d20"],
    complexity: 3,
    exampleConfig: { stat_system: "classic_six", generation: "standard_array", difficulty_scale: "standard", roll_frequency: "uncertain_only", advantage_system: "dnd_style", critical_effects: "standard", partial_success: true },
    genrePresets: {
      "classic_dnd": { stat_system: "classic_six", generation: "roll_4d6", difficulty_scale: "standard", roll_frequency: "uncertain_only" },
      "narrative_light": { stat_system: "simple_three", generation: "standard_array", difficulty_scale: "easy", roll_frequency: "dramatic_only" },
      "tactical_crunch": { stat_system: "classic_six", generation: "point_buy", difficulty_scale: "hard", roll_frequency: "every_action" },
    },
    stateVariables: ["player_stats"],
  },
  configSchema: [
    {
      key: "stat_system",
      label: "Stat System",
      type: "select",
      options: [
        {
          value: "classic_six",
          label: "Classic 6 (D&D)",
          description: "STR, DEX, CON, INT, WIS, CHA — the gold standard for fantasy RPGs. Each stat has clear mechanical applications: STR→melee/athletics, DEX→ranged/stealth/initiative, CON→HP/endurance, INT→arcana/investigation, WIS→perception/insight, CHA→persuasion/deception.",
        },
        {
          value: "simple_three",
          label: "Simple 3 (Body/Mind/Spirit)",
          description: "Streamlined for narrative-first games. Body covers all physical actions, Mind covers intellect and awareness, Spirit covers social and willpower. Fewer stats means faster resolution but less granularity.",
        },
        {
          value: "five_stat",
          label: "Five Stat (Might/Agility/Wits/Presence/Endurance)",
          description: "A balanced middle ground. Might for power, Agility for speed and finesse, Wits for intellect and perception, Presence for social and leadership, Endurance for toughness and stamina.",
        },
        {
          value: "custom",
          label: "Custom (define below)",
          description: "Define your own ability scores. Enter comma-separated stat names in the custom stats field that appears below.",
        },
      ],
      default: "classic_six",
      description: "Which ability score framework governs your characters",
    },
    {
      key: "stat_list",
      label: "Custom Stats",
      type: "text",
      default: "strength,dexterity,constitution,intelligence,wisdom,charisma",
      description: "Comma-separated stat names (only used when Custom is selected above)",
      placeholder: "e.g. might,agility,wits,presence",
      condition: { key: "stat_system", value: "custom" },
    },
    {
      key: "generation",
      label: "Score Generation",
      type: "select",
      options: [
        {
          value: "standard_array",
          label: "Standard Array (15,14,13,12,10,8)",
          description: "Balanced and predictable. Every character has the same total stats — the player chooses WHERE to be strong. Best for fairness-focused games.",
        },
        {
          value: "roll_4d6",
          label: "Roll 4d6 Drop Lowest",
          description: "Classic D&D generation. Roll four d6, drop the lowest die for each stat. More variance — some characters will be naturally gifted, others challenged. Creates memorable stat spreads.",
        },
        {
          value: "heroic",
          label: "Heroic Array (18,16,14,12,10,8)",
          description: "Players start significantly above average. Every character feels powerful from the beginning. Good for epic-scale campaigns where heroes are exceptional from day one.",
        },
        {
          value: "point_buy",
          label: "Point Buy (27 points)",
          description: "27 points to buy scores starting at 8. Cost increases above 13 (14=7pts, 15=9pts). Maximum control — players optimize their build precisely. Best for tactical games.",
        },
      ],
      default: "standard_array",
    },
    {
      key: "difficulty_scale",
      label: "Check Difficulty Scale",
      type: "select",
      options: [
        {
          value: "easy",
          label: "Easy (DC 8–15)",
          description: "Heroes succeed most of the time. Failures are rare and dramatic. Good for narrative games where competence is assumed. Trivial=5, Easy=8, Medium=10, Hard=13, Very Hard=15.",
        },
        {
          value: "standard",
          label: "Standard (DC 10–20)",
          description: "The classic D&D scale. Trained characters succeed ~65% on medium tasks. Creates meaningful tension without constant failure. Trivial=5, Easy=10, Medium=15, Hard=18, Very Hard=20, Nearly Impossible=25.",
        },
        {
          value: "hard",
          label: "Hard (DC 12–25)",
          description: "Even trained characters face real risk. Every roll matters. Failures are common and must be planned for. Good for gritty survival or dark fantasy. Trivial=8, Easy=12, Medium=16, Hard=20, Very Hard=25.",
        },
        {
          value: "brutal",
          label: "Brutal (DC 15–30)",
          description: "Success is never guaranteed. Even experts struggle with medium tasks. Forces creative problem-solving over brute-force rolling. Reserved for punishing rogue-like experiences.",
        },
      ],
      default: "standard",
      description: "Sets the DC ranges the narrator uses for skill checks — this directly affects success rates",
    },
    {
      key: "roll_frequency",
      label: "Roll Frequency",
      type: "select",
      options: [
        {
          value: "every_action",
          label: "Every Action (Mandatory Dice)",
          description: "EVERY meaningful player action requires a skill_check roll. No auto-success. Attacking, persuading, picking locks, investigating, crafting — everything. The dice always decide. Most crunchy and unpredictable.",
        },
        {
          value: "uncertain_only",
          label: "Uncertain Only (Recommended)",
          description: "Roll only when the outcome is genuinely uncertain. A strong warrior breaks a wooden door without rolling, but picks a complex lock with dice. Balances narrative flow with mechanical stakes.",
        },
        {
          value: "dramatic_only",
          label: "Dramatic Moments Only",
          description: "Dice come out ONLY at high-stakes moments: combat, critical negotiations, life-or-death decisions. Everything else resolves narratively based on character competence. Most story-focused option.",
        },
        {
          value: "combat_only",
          label: "Combat Only",
          description: "Dice are exclusively for combat resolution. All social, exploration, and skill challenges resolve through pure narrative and character capabilities. Simplest option.",
        },
      ],
      default: "uncertain_only",
      description: "How often the narrator must use dice rolls to resolve player actions",
    },
    {
      key: "proficiency",
      label: "Proficiency Bonus",
      type: "boolean",
      default: true,
      description: "Trained skills get +2 bonus, scaling +1 every 4 levels (lv5→+3, lv9→+4, etc.)",
    },
    {
      key: "advantage_system",
      label: "Advantage / Disadvantage",
      type: "select",
      options: [
        {
          value: "dnd_style",
          label: "D&D Style (roll 2d20)",
          description: "Advantage: roll 2d20, take the higher. Disadvantage: roll 2d20, take the lower. Simple, dramatic swings. Situational bonuses (flanking, high ground, surprise) grant advantage instead of numeric modifiers.",
        },
        {
          value: "modifier",
          label: "Flat Modifier (+2 / -2)",
          description: "Favorable conditions add +2, unfavorable subtract -2. Stacks if multiple factors apply (max ±6). More granular than advantage but requires more bookkeeping.",
        },
        {
          value: "none",
          label: "None",
          description: "No advantage/disadvantage system. Every roll is a straight 1d20 + modifier vs DC. Simplest but loses situational nuance.",
        },
      ],
      default: "dnd_style",
      description: "How favorable/unfavorable conditions affect dice rolls",
    },
    {
      key: "critical_effects",
      label: "Critical Hit/Fail Effects",
      type: "select",
      options: [
        {
          value: "dramatic",
          label: "Dramatic (Strong Effects)",
          description: "Nat 20: exceptional outcome — bonus loot, extra information, enemy morale broken, permanent advantage gained. Nat 1: serious complication — weapon breaks, spell backfires, ally endangered, environmental hazard triggered.",
        },
        {
          value: "standard",
          label: "Standard (Moderate Effects)",
          description: "Nat 20: solid bonus — extra damage, additional clue, NPC impressed. Nat 1: minor complication — stumble, dropped item, attract attention. Memorable but not campaign-altering.",
        },
        {
          value: "minimal",
          label: "Minimal (Just Success/Fail)",
          description: "Nat 20: guaranteed success, but no extra bonus. Nat 1: guaranteed failure, but no additional complication. Keeps crits from dominating the narrative.",
        },
      ],
      default: "standard",
      description: "How impactful natural 20s and natural 1s are on the story",
    },
    {
      key: "partial_success",
      label: "Partial Success",
      type: "boolean",
      default: true,
      description: "When a roll is within 2 of the DC, allow partial success — you achieve the goal but with a complication, cost, or reduced effect",
    },
    {
      key: "contested_checks",
      label: "Contested Checks",
      type: "boolean",
      default: true,
      description: "When opposing an NPC (arm wrestling, persuasion vs insight, stealth vs perception), both sides roll and compare totals instead of rolling against a fixed DC",
    },
    {
      key: "group_checks",
      label: "Group Checks",
      type: "boolean",
      default: false,
      description: "In multiplayer: when the party attempts something together (sneaking, climbing), each player rolls and the group succeeds if at least half pass",
    },
    {
      key: "skill_specializations",
      label: "Skill Specializations",
      type: "textarea",
      default: "",
      description: "Optional: define specialized sub-skills that grant additional bonuses. One per line, format: 'stat:specialization' (e.g. 'DEX:lockpicking', 'CHA:intimidation'). Characters with matching specializations get +2 on top of their stat modifier.",
      placeholder: "DEX:lockpicking\nCHA:intimidation\nINT:arcana\nWIS:tracking\nSTR:climbing",
      rows: 4,
    },
    {
      key: "environmental_modifiers",
      label: "Environmental Skill Modifiers",
      type: "boolean",
      default: true,
      description: "Weather, lighting, and terrain automatically affect relevant checks (darkness→-5 perception, rain→-2 DEX, underwater→-4 fire magic). Cross-references Time & Environment mechanic when active.",
    },
    {
      key: "exhaustion_penalties",
      label: "Exhaustion Penalties",
      type: "boolean",
      default: true,
      description: "Low survival needs or excessive activity impose cumulative -1 to -3 penalties on all checks. Cross-references Survival mechanic when active.",
    },
  ],
  systemPromptSection: (config) => {
    const system = config.stat_system || "classic_six";
    const gen = config.generation || "standard_array";
    const scale = config.difficulty_scale || "standard";
    const prof = config.proficiency !== false;
    const advSystem = config.advantage_system || "dnd_style";
    const critEffects = config.critical_effects || "standard";
    const partialSuccess = config.partial_success !== false;
    const contested = config.contested_checks !== false;
    const groupChecks = config.group_checks === true;
    const envMods = config.environmental_modifiers !== false;
    const exhaustion = config.exhaustion_penalties !== false;
    const specializations = config.skill_specializations || "";

    let stats: string;
    let statApplications = "";
    if (system === "classic_six") {
      stats = "STR, DEX, CON, INT, WIS, CHA";
      statApplications = `\n- Stat applications: STR→melee/athletics/breaking, DEX→ranged/stealth/acrobatics/initiative/lockpicking, CON→HP/endurance/resist poison/concentration, INT→arcana/investigation/lore/crafting, WIS→perception/insight/survival/medicine/tracking, CHA→persuasion/deception/intimidation/performance/leadership.`;
    } else if (system === "simple_three") {
      stats = "Body, Mind, Spirit";
      statApplications = `\n- Stat applications: Body→all physical (combat, athletics, endurance, stealth), Mind→all mental (knowledge, perception, investigation, crafting), Spirit→all social/willpower (persuasion, intimidation, resist fear, leadership).`;
    } else if (system === "five_stat") {
      stats = "Might, Agility, Wits, Presence, Endurance";
      statApplications = `\n- Stat applications: Might→melee/breaking/lifting, Agility→ranged/stealth/acrobatics/initiative, Wits→perception/investigation/arcana/crafting, Presence→persuasion/intimidation/leadership/performance, Endurance→HP/resist effects/concentration/survival.`;
    } else {
      stats = (config.stat_list || "strength,dexterity,constitution,intelligence,wisdom,charisma").split(",").map((s: string) => s.trim()).join(", ");
    }

    const dcRanges: Record<string, string> = {
      easy: "Trivial:5, Easy:8, Medium:10, Hard:13, Very Hard:15",
      standard: "Trivial:5, Easy:10, Medium:15, Hard:18, Very Hard:20, Nearly Impossible:25",
      hard: "Trivial:8, Easy:12, Medium:16, Hard:20, Very Hard:25",
      brutal: "Trivial:10, Easy:15, Medium:18, Hard:22, Very Hard:28, Nearly Impossible:30",
    };

    const arrays: Record<string, string> = {
      standard_array: "Assign 15, 14, 13, 12, 10, 8 to abilities.",
      roll_4d6: "Roll 4d6, drop lowest for each stat.",
      heroic: "Assign 18, 16, 14, 12, 10, 8 to abilities.",
      point_buy: "27 points to buy scores (start at 8, cost increases above 13).",
    };

    const rollFreq = config.roll_frequency || "uncertain_only";
    const freqDesc: Record<string, string> = {
      every_action: `**MANDATORY DICE RESOLUTION**: You MUST use the skill_check tool for ANY meaningful player action — no auto-success allowed. Attacking, persuading, investigating, crafting, climbing — EVERYTHING. The dice always decide.`,
      uncertain_only: `**DICE FOR UNCERTAIN ACTIONS**: Roll only when the outcome is genuinely uncertain. A strong warrior breaks a thin door without rolling, but a complex lock requires dice.`,
      dramatic_only: `**DRAMATIC MOMENTS ONLY**: Dice come out ONLY at high-stakes moments — combat, critical negotiations, life-or-death. Everything else resolves narratively based on character stats.`,
      combat_only: `**COMBAT-ONLY DICE**: Roll dice ONLY during combat. All non-combat actions resolve narratively based on character stats and context.`,
    };

    const advDesc: Record<string, string> = {
      dnd_style: `- ADVANTAGE/DISADVANTAGE: Roll 2d20, take higher (advantage) or lower (disadvantage). Granted by: flanking, high ground, surprise, good roleplay, creative tactics. Stacking advantage/disadvantage cancels out (1 of each = normal roll).`,
      modifier: `- SITUATIONAL MODIFIERS: Favorable conditions grant +2, unfavorable -2. Multiple factors stack (max ±6). Examples: high ground +2, darkness -2, surprise +2, encumbered -2.`,
      none: `- No advantage/disadvantage system. All rolls are straight 1d20 + modifier vs DC.`,
    };

    const critDesc: Record<string, string> = {
      dramatic: `- CRITICAL EFFECTS: Nat 20 = EXCEPTIONAL outcome — bonus loot, extra information, enemy morale broken, permanent advantage. Nat 1 = SERIOUS complication — weapon breaks, spell backfires, ally endangered, environmental hazard. Make crits memorable and impactful.`,
      standard: `- CRITICAL EFFECTS: Nat 20 = solid bonus (extra damage, additional clue, NPC impressed). Nat 1 = minor complication (stumble, dropped item, attract attention). Notable but not campaign-altering.`,
      minimal: `- CRITICAL EFFECTS: Nat 20 = guaranteed success (no extra bonus). Nat 1 = guaranteed failure (no extra penalty). Crits are reliable but not dramatic.`,
    };

    return `## STATS & SKILL CHECKS (Rules — Mandatory)
- Abilities: ${stats}
- Generation: ${arrays[gen] || arrays.standard_array}
- Modifier = floor((score - 10) / 2). E.g. 14 → +2, 8 → −1.
- ${prof ? "Proficiency bonus: +2 at level 1, +1 every 4 levels (lv5→+3, lv9→+4). Applied to trained skills." : "No proficiency bonus."}
- Track ability scores in "player_stats" state variable.${statApplications}
- ROLL FREQUENCY: ${freqDesc[rollFreq]}
- SKILL CHECKS: Use skill_check tool for dice resolution.
  - Roll 1d20 + stat modifier (+ proficiency if trained) vs DC.
  - Difficulty scale (${scale}): ${dcRanges[scale]}
${advDesc[advSystem]}
${critDesc[critEffects]}
${partialSuccess ? `- PARTIAL SUCCESS: When total is within 2 of DC (below it), allow partial success — the goal is achieved but with a complication, cost, or reduced effect. Makes near-misses interesting.` : ""}
${contested ? `- CONTESTED CHECKS: When opposing an NPC directly (arm wrestling, persuasion vs insight, stealth vs perception), BOTH sides roll 1d20 + relevant modifier. Higher total wins. Ties favor the defender.` : ""}
${groupChecks ? `- GROUP CHECKS: When the party attempts something together, each player rolls. The group succeeds if at least half pass. One player can "carry" the group with a high roll.` : ""}
${specializations ? `- SPECIALIZATIONS: Characters with skill specializations get an additional +2 bonus:\n${specializations.split("\n").filter((s: string) => s.trim()).map((s: string) => `  - ${s.trim()}`).join("\n")}` : ""}
${envMods ? `- ENVIRONMENTAL MODIFIERS (cross-references Time & Environment):
  - Darkness/night: -5 to perception, -2 to ranged attacks
  - Rain/snow: -2 to DEX-based checks, slippery surfaces
  - Storm/blizzard: -2 to perception, disadvantage on ranged
  - Fog: -2 to perception, advantage on stealth
  - Extreme heat: -1 to CON checks per hour exposed
  - Underwater: -4 to fire-based abilities, -2 to non-aquatic actions` : ""}
${exhaustion ? `- EXHAUSTION PENALTIES (cross-references Survival):
  - Survival needs <30: -1 to all checks
  - Survival needs <15: -2 to all checks, disadvantage on physical
  - Survival needs 0 (any): -3 to all checks, can only take one action per turn` : ""}
- If the player hasn't set up stats, prompt them during character creation.
- Difficulty is modified by adaptive difficulty system when active. DC modifier applied automatically.`;
  },
  widgets: [
    {
      id: "dice_tray_inline",
      type: "inline",
      label: "Dice",
      icon: "Dices",
      component: "DiceTray",
      dataBindings: {
        playerStats: "playerChar.stats",
      },
      visibility: "when_active",
      priority: 20,
    },
  ],
  onEvent: (event) => {
    // React to weather changes for environmental modifiers
    if (event.type === "weather_changed") {
      const condition = event.data.condition;
      const notes: string[] = [];
      if (condition === "storm" || condition === "blizzard") {
        notes.push("⚔ Skill check modifier: -2 perception, disadvantage on ranged (severe weather)");
      } else if (condition === "rain" || condition === "snow") {
        notes.push("⚔ Skill check modifier: -2 DEX-based checks (wet/slippery conditions)");
      } else if (condition === "fog") {
        notes.push("⚔ Skill check modifier: -2 perception, advantage on stealth (fog)");
      }
      return notes.length > 0 ? { narrativeNotes: notes } : null;
    }
    // React to temperature extremes
    if (event.type === "temperature_extreme") {
      const extreme = event.data.extreme;
      return {
        narrativeNotes: [extreme === "cold" ? "❄ Skill check modifier: -2 DEX (numbing cold)" : "🔥 Skill check modifier: -1 CON (oppressive heat)"],
      };
    }
    return null;
  },
  tools: [
    {
      type: "function",
      function: {
        name: "skill_check",
        description: "Roll 1d20 + stat modifier against a difficulty class. Supports advantage/disadvantage and situational modifiers.",
        parameters: {
          type: "object",
          properties: {
            stat: { type: "string", description: "Which stat to use (e.g. STR, DEX, WIS, Body, Might)" },
            difficulty: { type: "number", description: "Difficulty class (DC). Will be auto-adjusted by difficulty modifier." },
            description: { type: "string", description: "What the player is attempting" },
            proficiency_applies: { type: "boolean", description: "Whether the character is trained in this skill" },
            advantage: { type: "boolean", description: "Roll with advantage (2d20 take higher)" },
            disadvantage: { type: "boolean", description: "Roll with disadvantage (2d20 take lower)" },
            situational_modifier: { type: "number", description: "Additional +/- modifier from environmental or situational factors" },
            contested: { type: "boolean", description: "Is this a contested check? If true, an NPC also rolls." },
            opponent_modifier: { type: "number", description: "NPC's total modifier for contested checks" },
          },
          required: ["stat", "difficulty", "description"],
        },
      },
    },
  ],
  initializeState: () => ({
    proficiency_bonus: 2,
  }),
};
